// src/cockpit4/components/DouyinLiveStrategyPanel.tsx
// ============================================
// 抖音直播策略面板 - 营销中心独立子模块
//
// 白皮书规格：营销中心包含抖音直播策略子模块
//   四个部分：指标 / 规划 / 准备清单 / 复盘
//
// 数据来源（Obsidian Vault 运营层 Live 目录）：
//   - 指标：     10_Operations（运营层）/Live/Metrics-{date}.md
//   - 规划：     10_Operations（运营层）/Live/Plan-{date}.md
//   - 准备清单： 10_Operations（运营层）/Live/Checklist-{date}.md
//   - 复盘：     10_Operations（运营层）/Live/Review-{date}.md
//
// 白皮书铁律一：无数据显示"暂无数据"，不展示假数据。
// ============================================

import { useEffect, useState, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Video,
  Target,
  Calendar,
  ClipboardList,
  TrendingUp,
  RefreshCw,
  Loader2,
  AlertCircle,
  Eye,
  Heart,
  DollarSign,
  Save,
  Edit3,
  CheckCircle2,
  Circle,
  Lightbulb,
  Rocket,
  Clock,
  Users,
} from "lucide-react";

// ============================================
// 配置
// ============================================

const VAULT_LIVE_DIR = "10_Operations（运营层）/Live";

type LiveTab = "metrics" | "plan" | "checklist" | "review";

const TAB_CONFIG: { id: LiveTab; label: string; icon: typeof Target }[] = [
  { id: "metrics", label: "指标", icon: Target },
  { id: "plan", label: "规划", icon: Calendar },
  { id: "checklist", label: "准备清单", icon: ClipboardList },
  { id: "review", label: "复盘", icon: TrendingUp },
];

// ============================================
// Vault 读写工具
// ============================================

/** 读取 Vault 文件内容，失败或不存在返回 null */
async function readVault(path: string): Promise<string | null> {
  try {
    const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`);
    if (!resp.ok) return null;
    const result = await resp.json();
    if (result.success && result.content) return result.content as string;
    return null;
  } catch {
    return null;
  }
}

/** 写入 Vault 文件，返回是否成功 */
async function writeVault(path: string, content: string): Promise<boolean> {
  try {
    const resp = await fetch(`/vault-api/write?path=${encodeURIComponent(path)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    });
    if (!resp.ok) return false;
    const result = await resp.json();
    return !!result.success;
  } catch {
    return false;
  }
}

/** 获取今日日期字符串 YYYY-MM-DD */
function getTodayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

// ============================================
// 指标数据解析
// ============================================

interface MetricsData {
  /** 流量预测 */
  traffic: { views: string; peak: string; stay: string };
  /** 互动指标目标 */
  interaction: { likes: string; comments: string; shares: string; followRate: string };
  /** 转化指标目标 */
  conversion: { gmv: string; aov: string; convRate: string; refundRate: string };
}

/** 从 Markdown 解析指标数据（兼容无 frontmatter 的情况，只要有指标标题即尝试解析） */
function parseMetrics(content: string): MetricsData | null {
  const data: MetricsData = {
    traffic: { views: "", peak: "", stay: "" },
    interaction: { likes: "", comments: "", shares: "", followRate: "" },
    conversion: { gmv: "", aov: "", convRate: "", refundRate: "" },
  };

  // 通用键值提取：匹配 "- 字段名: 值"
  const kv = (block: string, key: string): string => {
    const m = block.match(new RegExp(`-\\s*${key}\\s*[:：]\\s*(.+)`, "i"));
    return m ? m[1].trim() : "";
  };

  // 按 ## 标题切分
  const sections = content.split(/^##\s+/m);
  for (const sec of sections) {
    const lower = sec.toLowerCase();
    if (sec.startsWith("流量") || /traffic/i.test(lower)) {
      data.traffic.views = kv(sec, "预估观看人数|观看人数|观看");
      data.traffic.peak = kv(sec, "在线峰值|峰值");
      data.traffic.stay = kv(sec, "平均停留时长|停留时长");
    } else if (sec.startsWith("互动") || /interaction/i.test(lower)) {
      data.interaction.likes = kv(sec, "点赞数|点赞");
      data.interaction.comments = kv(sec, "评论数|评论");
      data.interaction.shares = kv(sec, "分享数|分享");
      data.interaction.followRate = kv(sec, "关注转化率|转化率");
    } else if (sec.startsWith("转化") || /conversion/i.test(lower)) {
      data.conversion.gmv = kv(sec, "GMV|gmv");
      data.conversion.aov = kv(sec, "客单价");
      data.conversion.convRate = kv(sec, "转化率");
      data.conversion.refundRate = kv(sec, "退款率");
    }
  }

  // 至少有一个字段有值才算有效数据
  const hasData =
    data.traffic.views ||
    data.traffic.peak ||
    data.traffic.stay ||
    data.interaction.likes ||
    data.interaction.comments ||
    data.interaction.shares ||
    data.interaction.followRate ||
    data.conversion.gmv ||
    data.conversion.aov ||
    data.conversion.convRate ||
    data.conversion.refundRate;
  return hasData ? data : null;
}

// ============================================
// 规划数据解析与序列化
// ============================================

interface PlanData {
  theme: string;
  startTime: string;
  duration: string;
  /** 话术节奏（开场/产品介绍/互动/逼单/收尾） */
  scripts: { stage: string; time: string; content: string }[];
  /** 投流建议 */
  adBudget: string;
  adAudience: string;
  adBid: string;
  /** 主播排班 */
  host: string;
  coHost: string;
  operator: string;
}

/** 从 Markdown 解析规划数据 */
function parsePlan(content: string): PlanData | null {
  const plan: PlanData = {
    theme: "",
    startTime: "",
    duration: "",
    scripts: [],
    adBudget: "",
    adAudience: "",
    adBid: "",
    host: "",
    coHost: "",
    operator: "",
  };

  const kv = (block: string, key: string): string => {
    const m = block.match(new RegExp(`-\\s*${key}\\s*[:：]\\s*(.+)`, "i"));
    return m ? m[1].trim() : "";
  };

  // 按一级标题切分
  const sections = content.split(/^##\s+/m);
  for (const sec of sections) {
    const titleLine = sec.split("\n")[0].trim();
    if (titleLine.startsWith("基本信息") || /主题/.test(titleLine)) {
      plan.theme = kv(sec, "主题");
      plan.startTime = kv(sec, "开始时间|时间");
      plan.duration = kv(sec, "时长");
    } else if (titleLine.startsWith("话术节奏") || /话术/.test(titleLine)) {
      // 解析 ### 子阶段
      const subParts = sec.split(/^###\s+/m);
      for (const sub of subParts) {
        if (!sub.trim()) continue;
        const firstLine = sub.split("\n")[0].trim();
        // 阶段名 (时间段) 格式
        const m = firstLine.match(/^(.+?)\s*[（(]\s*(.+?)\s*[）)]\s*$/);
        if (m) {
          plan.scripts.push({
            stage: m[1].trim(),
            time: m[2].trim(),
            content: sub.split("\n").slice(1).join("\n").trim(),
          });
        }
      }
    } else if (titleLine.startsWith("投流") || /投流/.test(titleLine)) {
      plan.adBudget = kv(sec, "千川预算|预算");
      plan.adAudience = kv(sec, "定向人群|人群");
      plan.adBid = kv(sec, "出价策略|出价");
    } else if (titleLine.startsWith("主播排班") || /排班/.test(titleLine)) {
      plan.host = kv(sec, "主播");
      plan.coHost = kv(sec, "助播");
      plan.operator = kv(sec, "运营");
    }
  }

  const hasData =
    plan.theme ||
    plan.startTime ||
    plan.duration ||
    plan.scripts.length > 0 ||
    plan.adBudget ||
    plan.adAudience ||
    plan.adBid ||
    plan.host ||
    plan.coHost ||
    plan.operator;
  return hasData ? plan : null;
}

/** 将规划数据序列化为 Markdown */
function serializePlan(plan: PlanData, date: string): string {
  const scriptsBlock =
    plan.scripts.length > 0
      ? plan.scripts
          .map(
            (s) =>
              `### ${s.stage} (${s.time || "—"})\n${s.content || "待补充"}`
          )
          .join("\n\n")
      : "### 开场 (00:00-00:10)\n待补充\n\n### 产品介绍 (00:10-00:40)\n待补充\n\n### 互动 (00:40-01:00)\n待补充\n\n### 逼单 (01:00-01:45)\n待补充\n\n### 收尾 (01:45-02:00)\n待补充";

  return `---
type: live_plan
date: ${date}
created_at: ${new Date().toISOString()}
---

# 直播规划 · ${date}

## 基本信息
- 主题: ${plan.theme || "待定"}
- 开始时间: ${plan.startTime || "待定"}
- 时长: ${plan.duration || "待定"}

## 话术节奏
${scriptsBlock}

## 投流建议
- 千川预算: ${plan.adBudget || "待定"}
- 定向人群: ${plan.adAudience || "待定"}
- 出价策略: ${plan.adBid || "待定"}

## 主播排班
- 主播: ${plan.host || "待定"}
- 助播: ${plan.coHost || "待定"}
- 运营: ${plan.operator || "待定"}

---
*由 XEAOS 抖音直播策略面板生成 · 数据来源：Obsidian Vault*
`;
}

// ============================================
// 准备清单数据解析与序列化
// ============================================

interface ChecklistItem {
  text: string;
  done: boolean;
}
interface ChecklistGroup {
  name: string;
  items: ChecklistItem[];
}
interface ChecklistData {
  groups: ChecklistGroup[];
}

/** 从 Markdown 解析清单数据 */
function parseChecklist(content: string): ChecklistData | null {
  const groups: ChecklistGroup[] = [];
  const sections = content.split(/^##\s+/m);
  for (const sec of sections) {
    const firstLine = sec.split("\n")[0].trim();
    if (!firstLine) continue;
    // 跳过文档主标题（# 开头，非 ## 分组）和 frontmatter 段
    if (firstLine.startsWith("#")) continue;
    const items: ChecklistItem[] = [];
    const lines = sec.split("\n").slice(1);
    for (const line of lines) {
      // 匹配 - [x] 或 - [ ] 语法
      const m = line.match(/^-\s*\[([ xX])\]\s*(.+)/);
      if (m) {
        items.push({ done: m[1].toLowerCase() === "x", text: m[2].trim() });
      }
    }
    if (items.length > 0) {
      groups.push({ name: firstLine, items });
    }
  }
  return groups.length > 0 ? { groups } : null;
}

/** 将清单数据序列化为 Markdown */
function serializeChecklist(data: ChecklistData, date: string): string {
  const groupsBlock = data.groups
    .map(
      (g) =>
        `## ${g.name}\n${g.items
          .map((it) => `- [${it.done ? "x" : " "}] ${it.text}`)
          .join("\n")}`
    )
    .join("\n\n");

  return `---
type: live_checklist
date: ${date}
created_at: ${new Date().toISOString()}
---

# 直播准备清单 · ${date}

${groupsBlock}

---
*由 XEAOS 抖音直播策略面板生成 · 数据来源：Obsidian Vault*
`;
}

// ============================================
// 复盘数据解析
// ============================================

interface ReviewRow {
  metric: string;
  target: string;
  actual: string;
  rate: string;
}
interface ReviewData {
  /** 实际 vs 目标对比表 */
  rows: ReviewRow[];
  /** 亮点 */
  highlights: string[];
  /** 不足 */
  shortcomings: string[];
  /** 改进措施 */
  improvements: string[];
}

/** 从 Markdown 解析复盘数据 */
function parseReview(content: string): ReviewData | null {
  const data: ReviewData = { rows: [], highlights: [], shortcomings: [], improvements: [] };
  const sections = content.split(/^##\s+/m);
  for (const sec of sections) {
    const title = sec.split("\n")[0].trim();
    const body = sec.split("\n").slice(1).join("\n");
    if (title.startsWith("实际") || /vs|对比/i.test(title)) {
      // 解析 Markdown 表格
      const tableLines = body.split("\n").filter((l) => l.trim().startsWith("|"));
      // 跳过表头和分隔行
      for (const line of tableLines.slice(2)) {
        const cells = line.split("|").map((c) => c.trim()).filter((c) => c.length > 0);
        if (cells.length >= 2) {
          data.rows.push({
            metric: cells[0] || "",
            target: cells[1] || "",
            actual: cells[2] || "",
            rate: cells[3] || "",
          });
        }
      }
    } else if (title.startsWith("亮点")) {
      data.highlights = body.split("\n").map((l) => l.replace(/^[-*]\s*/, "").trim()).filter(Boolean);
    } else if (title.startsWith("不足")) {
      data.shortcomings = body.split("\n").map((l) => l.replace(/^[-*]\s*/, "").trim()).filter(Boolean);
    } else if (title.startsWith("改进")) {
      data.improvements = body.split("\n").map((l) => l.replace(/^[-*]\s*/, "").trim()).filter(Boolean);
    }
  }

  const hasData =
    data.rows.length > 0 ||
    data.highlights.length > 0 ||
    data.shortcomings.length > 0 ||
    data.improvements.length > 0;
  return hasData ? data : null;
}

// ============================================
// 通用 UI 子组件
// ============================================

/** 加载中占位 */
function LoadingBlock({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center py-8">
      <Loader2 size={14} className="animate-spin text-cyan-400" />
      <span className="ml-2 text-[11px] font-mono text-gray-500">{text}</span>
    </div>
  );
}

/** 空数据占位（白皮书铁律一：不展示假数据） */
function EmptyBlock({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <AlertCircle size={18} className="text-gray-600 mb-1.5" />
      <span className="text-[11px] font-mono text-gray-500">{text}</span>
    </div>
  );
}

/** 错误提示 */
function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="px-3 py-2 rounded bg-red-500/10 border border-red-500/20">
      <span className="text-[11px] font-mono text-red-400">{message}</span>
    </div>
  );
}

/** 数据卡片（玻璃拟态） */
function DataCard({
  title,
  icon: Icon,
  accent,
  children,
}: {
  title: string;
  icon: typeof Target;
  accent: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={12} className={accent} />
        <span className="text-[11px] font-mono uppercase tracking-wider text-gray-300">
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

/** 指标行 */
function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04]">
      <span className="text-[10px] text-gray-500">{label}</span>
      <span className="text-[11px] font-mono text-cyan-300">
        {value || "—"}
      </span>
    </div>
  );
}

// ============================================
// 指标 Tab
// ============================================

function MetricsTab({ date, refreshKey }: { date: string; refreshKey: number }) {
  const [data, setData] = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const path = `${VAULT_LIVE_DIR}/Metrics-${date}.md`;
    const content = await readVault(path);
    if (content === null) {
      setData(null);
    } else {
      setData(parseMetrics(content));
    }
    setLoading(false);
  }, [date]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) return <LoadingBlock text="加载直播指标..." />;
  if (error) return <ErrorBlock message={error} />;
  if (!data) return <EmptyBlock text="暂无直播指标数据" />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="grid grid-cols-3 gap-2"
    >
      {/* 流量预测 */}
      <DataCard title="流量预测" icon={Eye} accent="text-cyan-400">
        <div className="space-y-1">
          <MetricRow label="预估观看人数" value={data.traffic.views} />
          <MetricRow label="在线峰值" value={data.traffic.peak} />
          <MetricRow label="平均停留时长" value={data.traffic.stay} />
        </div>
      </DataCard>

      {/* 互动指标目标 */}
      <DataCard title="互动指标目标" icon={Heart} accent="text-pink-400">
        <div className="space-y-1">
          <MetricRow label="点赞数" value={data.interaction.likes} />
          <MetricRow label="评论数" value={data.interaction.comments} />
          <MetricRow label="分享数" value={data.interaction.shares} />
          <MetricRow label="关注转化率" value={data.interaction.followRate} />
        </div>
      </DataCard>

      {/* 转化指标目标 */}
      <DataCard title="转化指标目标" icon={DollarSign} accent="text-emerald-400">
        <div className="space-y-1">
          <MetricRow label="GMV" value={data.conversion.gmv} />
          <MetricRow label="客单价" value={data.conversion.aov} />
          <MetricRow label="转化率" value={data.conversion.convRate} />
          <MetricRow label="退款率" value={data.conversion.refundRate} />
        </div>
      </DataCard>

      {/* 指标图标图例 */}
      <div className="col-span-3 flex items-center justify-end gap-3 text-[9px] font-mono text-gray-600 mt-1">
        <span className="flex items-center gap-1"><Eye size={9} className="text-cyan-400" />流量</span>
        <span className="flex items-center gap-1"><Heart size={9} className="text-pink-400" />互动</span>
        <span className="flex items-center gap-1"><DollarSign size={9} className="text-emerald-400" />转化</span>
        <span className="ml-2">数据来源：Obsidian Vault · Live/Metrics-{date}.md</span>
      </div>
    </motion.div>
  );
}

// ============================================
// 规划 Tab
// ============================================

function PlanTab({ date, refreshKey }: { date: string; refreshKey: number }) {
  const [data, setData] = useState<PlanData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<PlanData | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const path = `${VAULT_LIVE_DIR}/Plan-${date}.md`;
    const content = await readVault(path);
    const parsed = content === null ? null : parsePlan(content);
    setData(parsed);
    setDraft(parsed);
    setLoading(false);
  }, [date]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    const path = `${VAULT_LIVE_DIR}/Plan-${date}.md`;
    const ok = await writeVault(path, serializePlan(draft, date));
    setSaving(false);
    if (ok) {
      setData(draft);
      setEditing(false);
    } else {
      alert("保存失败，请检查 Vault 写入权限");
    }
  };

  if (loading) return <LoadingBlock text="加载直播规划..." />;

  // 无数据且非编辑模式：显示空状态 + 创建按钮
  if (!data && !editing) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center gap-3">
        <EmptyBlock text="暂无直播规划数据" />
        <button
          onClick={() => {
            setDraft({
              theme: "",
              startTime: "",
              duration: "",
              scripts: [
                { stage: "开场", time: "00:00-00:10", content: "" },
                { stage: "产品介绍", time: "00:10-00:40", content: "" },
                { stage: "互动", time: "00:40-01:00", content: "" },
                { stage: "逼单", time: "01:00-01:45", content: "" },
                { stage: "收尾", time: "01:45-02:00", content: "" },
              ],
              adBudget: "",
              adAudience: "",
              adBid: "",
              host: "",
              coHost: "",
              operator: "",
            });
            setEditing(true);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded transition-colors"
        >
          <Edit3 size={11} />
          创建直播规划
        </button>
      </div>
    );
  }

  // 编辑模式
  if (editing && draft) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-2"
      >
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-mono uppercase tracking-wider text-cyan-300">
            编辑直播规划
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setDraft(data);
                setEditing(false);
              }}
              className="px-2 py-1 text-[10px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
              保存到 Vault
            </button>
          </div>
        </div>

        {/* 基本信息表单（主题/时间/预算） */}
        <DataCard title="基本信息" icon={Calendar} accent="text-cyan-400">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">直播主题</label>
              <input
                value={draft.theme}
                onChange={(e) => setDraft({ ...draft, theme: e.target.value })}
                placeholder="例：夏日好物推荐"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">开始时间</label>
              <input
                value={draft.startTime}
                onChange={(e) => setDraft({ ...draft, startTime: e.target.value })}
                placeholder="例：2026-07-31 20:00"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">时长</label>
              <input
                value={draft.duration}
                onChange={(e) => setDraft({ ...draft, duration: e.target.value })}
                placeholder="例：120分钟"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
          </div>
        </DataCard>

        {/* 投流建议表单（预算） */}
        <DataCard title="投流建议" icon={Rocket} accent="text-purple-400">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">千川投流预算</label>
              <input
                value={draft.adBudget}
                onChange={(e) => setDraft({ ...draft, adBudget: e.target.value })}
                placeholder="例：3000元"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">定向人群</label>
              <input
                value={draft.adAudience}
                onChange={(e) => setDraft({ ...draft, adAudience: e.target.value })}
                placeholder="例：25-35岁女性"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">出价策略</label>
              <input
                value={draft.adBid}
                onChange={(e) => setDraft({ ...draft, adBid: e.target.value })}
                placeholder="例：自动出价"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
          </div>
        </DataCard>

        {/* 主播排班 */}
        <DataCard title="主播排班" icon={Users} accent="text-amber-400">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">主播</label>
              <input
                value={draft.host}
                onChange={(e) => setDraft({ ...draft, host: e.target.value })}
                placeholder="主播姓名"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">助播</label>
              <input
                value={draft.coHost}
                onChange={(e) => setDraft({ ...draft, coHost: e.target.value })}
                placeholder="助播姓名"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 block mb-0.5">运营</label>
              <input
                value={draft.operator}
                onChange={(e) => setDraft({ ...draft, operator: e.target.value })}
                placeholder="运营姓名"
                className="w-full px-2 py-1 text-[11px] font-mono text-gray-200 bg-black/30 border border-white/[0.08] rounded focus:outline-none focus:border-cyan-500/40"
              />
            </div>
          </div>
        </DataCard>

        {/* 话术节奏（只读展示，编辑内容较长不在表单内编辑） */}
        <DataCard title="话术节奏安排" icon={Clock} accent="text-cyan-400">
          <div className="space-y-1">
            {draft.scripts.map((s, i) => (
              <div key={i} className="flex items-start gap-2 px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04]">
                <span className="text-[10px] font-mono text-cyan-300 min-w-[80px]">{s.stage}</span>
                <span className="text-[10px] font-mono text-gray-500 min-w-[110px]">{s.time}</span>
                <span className="text-[10px] text-gray-400 flex-1">{s.content || "待补充"}</span>
              </div>
            ))}
          </div>
          <p className="text-[9px] font-mono text-gray-600 mt-1.5">
            话术详细内容请在 Obsidian Vault 中编辑 Plan-{date}.md 文件
          </p>
        </DataCard>
      </motion.div>
    );
  }

  // 展示模式
  const plan = data!;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-2"
    >
      <div className="flex items-center justify-end">
        <button
          onClick={() => {
            setDraft(data);
            setEditing(true);
          }}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded transition-colors"
        >
          <Edit3 size={10} />
          编辑
        </button>
      </div>

      {/* 基本信息 */}
      <DataCard title="直播主题 / 时间 / 时长" icon={Calendar} accent="text-cyan-400">
        <div className="grid grid-cols-3 gap-2">
          <MetricRow label="主题" value={plan.theme} />
          <MetricRow label="开始时间" value={plan.startTime} />
          <MetricRow label="时长" value={plan.duration} />
        </div>
      </DataCard>

      {/* 话术节奏安排 */}
      <DataCard title="话术节奏安排" icon={Clock} accent="text-cyan-400">
        {plan.scripts.length === 0 ? (
          <p className="text-[10px] font-mono text-gray-600">暂无话术节奏数据</p>
        ) : (
          <div className="space-y-1">
            {plan.scripts.map((s, i) => (
              <div key={i} className="flex items-start gap-2 px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04]">
                <span className="text-[10px] font-mono text-cyan-300 min-w-[80px]">{s.stage}</span>
                <span className="text-[10px] font-mono text-gray-500 min-w-[110px]">{s.time}</span>
                <span className="text-[10px] text-gray-400 flex-1 whitespace-pre-wrap">{s.content || "待补充"}</span>
              </div>
            ))}
          </div>
        )}
      </DataCard>

      {/* 投流建议 */}
      <div className="grid grid-cols-2 gap-2">
        <DataCard title="投流建议" icon={Rocket} accent="text-purple-400">
          <div className="space-y-1">
            <MetricRow label="千川投流预算" value={plan.adBudget} />
            <MetricRow label="定向人群" value={plan.adAudience} />
            <MetricRow label="出价策略" value={plan.adBid} />
          </div>
        </DataCard>

        {/* 主播排班 */}
        <DataCard title="主播排班" icon={Users} accent="text-amber-400">
          <div className="space-y-1">
            <MetricRow label="主播" value={plan.host} />
            <MetricRow label="助播" value={plan.coHost} />
            <MetricRow label="运营" value={plan.operator} />
          </div>
        </DataCard>
      </div>

      <p className="text-[9px] font-mono text-gray-600 text-right">
        数据来源：Obsidian Vault · Live/Plan-{date}.md
      </p>
    </motion.div>
  );
}

// ============================================
// 准备清单 Tab
// ============================================

function ChecklistTab({ date, refreshKey }: { date: string; refreshKey: number }) {
  const [data, setData] = useState<ChecklistData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedHint, setSavedHint] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const path = `${VAULT_LIVE_DIR}/Checklist-${date}.md`;
    const content = await readVault(path);
    if (content === null) {
      setData(null);
    } else {
      setData(parseChecklist(content));
    }
    setLoading(false);
  }, [date]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  /** 切换某一项的勾选状态，并保存回 Vault */
  const toggleItem = async (groupIdx: number, itemIdx: number) => {
    if (!data) return;
    const newGroups = data.groups.map((g, gi) =>
      gi === groupIdx
        ? {
            ...g,
            items: g.items.map((it, ii) =>
              ii === itemIdx ? { ...it, done: !it.done } : it
            ),
          }
        : g
    );
    const newData = { groups: newGroups };
    setData(newData);
    setSaving(true);
    const path = `${VAULT_LIVE_DIR}/Checklist-${date}.md`;
    const ok = await writeVault(path, serializeChecklist(newData, date));
    setSaving(false);
    if (ok) {
      setSavedHint(true);
      setTimeout(() => setSavedHint(false), 1500);
    }
  };

  if (loading) return <LoadingBlock text="加载准备清单..." />;
  if (!data) return <EmptyBlock text="暂无直播准备清单数据" />;

  // 计算完成进度
  const total = data.groups.reduce((sum, g) => sum + g.items.length, 0);
  const done = data.groups.reduce(
    (sum, g) => sum + g.items.filter((it) => it.done).length,
    0
  );
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-2"
    >
      {/* 进度条 */}
      <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <ClipboardList size={12} className="text-cyan-400" />
          <span className="text-[11px] font-mono text-gray-300">
            准备进度 {done}/{total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-32 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <span className="text-[11px] font-mono text-cyan-300 min-w-[36px] text-right">{progress}%</span>
          {saving && <Loader2 size={10} className="animate-spin text-gray-500" />}
          {savedHint && <CheckCircle2 size={11} className="text-emerald-400" />}
        </div>
      </div>

      {/* 清单分组 */}
      <div className="grid grid-cols-2 gap-2">
        {data.groups.map((group, gi) => (
          <DataCard key={gi} title={group.name} icon={CheckCircle2} accent="text-emerald-400">
            <div className="space-y-1">
              {group.items.map((item, ii) => (
                <button
                  key={ii}
                  onClick={() => toggleItem(gi, ii)}
                  className="w-full flex items-center gap-2 px-2 py-1 rounded bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] transition-colors text-left"
                >
                  {item.done ? (
                    <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" />
                  ) : (
                    <Circle size={12} className="text-gray-600 flex-shrink-0" />
                  )}
                  <span
                    className={`text-[11px] flex-1 ${
                      item.done ? "text-gray-500 line-through" : "text-gray-300"
                    }`}
                  >
                    {item.text}
                  </span>
                </button>
              ))}
            </div>
          </DataCard>
        ))}
      </div>

      <p className="text-[9px] font-mono text-gray-600 text-right">
        数据来源：Obsidian Vault · Live/Checklist-{date}.md（勾选自动保存）
      </p>
    </motion.div>
  );
}

// ============================================
// 复盘 Tab
// ============================================

function ReviewTab({ date, refreshKey }: { date: string; refreshKey: number }) {
  const [data, setData] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const path = `${VAULT_LIVE_DIR}/Review-${date}.md`;
    const content = await readVault(path);
    if (content === null) {
      setData(null);
    } else {
      setData(parseReview(content));
    }
    setLoading(false);
  }, [date]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) return <LoadingBlock text="加载复盘数据..." />;
  if (!data) return <EmptyBlock text="暂无复盘数据" />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-2"
    >
      {/* 实际数据 vs 目标对比表格 */}
      <DataCard title="实际数据 vs 目标对比" icon={TrendingUp} accent="text-cyan-400">
        {data.rows.length === 0 ? (
          <p className="text-[10px] font-mono text-gray-600">暂无对比数据</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left py-1 px-2 text-gray-500 font-normal">指标</th>
                  <th className="text-right py-1 px-2 text-gray-500 font-normal">目标</th>
                  <th className="text-right py-1 px-2 text-gray-500 font-normal">实际</th>
                  <th className="text-right py-1 px-2 text-gray-500 font-normal">达成率</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => {
                  // 达成率颜色：>=100% 绿色，>=80% 黄色，否则红色
                  const rateNum = parseFloat((row.rate || "").replace(/[^\d.]/g, ""));
                  const rateColor = isNaN(rateNum)
                    ? "text-gray-400"
                    : rateNum >= 100
                    ? "text-emerald-400"
                    : rateNum >= 80
                    ? "text-yellow-400"
                    : "text-red-400";
                  return (
                    <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                      <td className="py-1 px-2 text-gray-300">{row.metric}</td>
                      <td className="py-1 px-2 text-right text-gray-400">{row.target}</td>
                      <td className="py-1 px-2 text-right text-cyan-300">{row.actual}</td>
                      <td className={`py-1 px-2 text-right ${rateColor}`}>{row.rate}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DataCard>

      {/* 亮点 / 不足 / 改进措施 */}
      <div className="grid grid-cols-3 gap-2">
        <DataCard title="亮点" icon={Lightbulb} accent="text-yellow-400">
          {data.highlights.length === 0 ? (
            <p className="text-[10px] font-mono text-gray-600">暂无</p>
          ) : (
            <ul className="space-y-1">
              {data.highlights.map((h, i) => (
                <li key={i} className="text-[10px] text-gray-300 leading-relaxed flex gap-1">
                  <span className="text-yellow-400">•</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
        </DataCard>

        <DataCard title="不足" icon={AlertCircle} accent="text-red-400">
          {data.shortcomings.length === 0 ? (
            <p className="text-[10px] font-mono text-gray-600">暂无</p>
          ) : (
            <ul className="space-y-1">
              {data.shortcomings.map((s, i) => (
                <li key={i} className="text-[10px] text-gray-300 leading-relaxed flex gap-1">
                  <span className="text-red-400">•</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          )}
        </DataCard>

        <DataCard title="改进措施" icon={Rocket} accent="text-cyan-400">
          {data.improvements.length === 0 ? (
            <p className="text-[10px] font-mono text-gray-600">暂无</p>
          ) : (
            <ul className="space-y-1">
              {data.improvements.map((im, i) => (
                <li key={i} className="text-[10px] text-gray-300 leading-relaxed flex gap-1">
                  <span className="text-cyan-400">•</span>
                  <span>{im}</span>
                </li>
              ))}
            </ul>
          )}
        </DataCard>
      </div>

      <p className="text-[9px] font-mono text-gray-600 text-right">
        数据来源：Obsidian Vault · Live/Review-{date}.md
      </p>
    </motion.div>
  );
}

// ============================================
// 主组件
// ============================================

export default function DouyinLiveStrategyPanel() {
  const [activeTab, setActiveTab] = useState<LiveTab>("metrics");
  const [date] = useState<string>(getTodayStr());
  // 刷新触发器：递增后传入各 Tab，触发 useEffect 重新加载
  const [refreshKey, setRefreshKey] = useState(0);

  // 当前激活 Tab 的标题配置（用于 header 显示）
  const currentTab = useMemo(() => TAB_CONFIG.find((t) => t.id === activeTab)!, [activeTab]);
  const CurrentTabIcon = currentTab.icon;

  return (
    <div
      className="rounded-lg border border-cyan-500/20 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0B1220 0%, #1A2332 100%)",
      }}
    >
      {/* Header：标题 + Tab 切换 */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-cyan-500/15 bg-black/20">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <Video size={12} className="text-cyan-400" />
          </div>
          <div>
            <span className="text-[12px] font-semibold text-white tracking-tight">
              抖音直播策略
            </span>
            <span className="text-[9px] font-mono text-cyan-500/70 uppercase tracking-wider ml-1.5">
              Douyin Live Strategy
            </span>
          </div>
        </div>

        {/* Tab 切换 */}
        <div className="flex items-center gap-1">
          {TAB_CONFIG.map((tab) => {
            const active = tab.id === activeTab;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider rounded transition-all ${
                  active
                    ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                    : "text-gray-500 hover:text-gray-300 border border-transparent hover:bg-white/[0.03]"
                }`}
              >
                <Icon size={10} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab 内容区 */}
      <div className="p-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.2 }}
          >
            {/* 顶部信息条：当前 Tab + 日期 */}
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[10px] font-mono text-gray-500 flex items-center">
                <CurrentTabIcon size={10} className="text-cyan-400 mr-1" />
                {currentTab.label} · {date}
              </span>
              <button
                onClick={() => setRefreshKey((k) => k + 1)}
                className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono text-gray-500 hover:text-cyan-300 transition-colors"
                title="刷新当前 Tab"
              >
                <RefreshCw size={9} />
                刷新
              </button>
            </div>

            {activeTab === "metrics" && <MetricsTab date={date} refreshKey={refreshKey} />}
            {activeTab === "plan" && <PlanTab date={date} refreshKey={refreshKey} />}
            {activeTab === "checklist" && <ChecklistTab date={date} refreshKey={refreshKey} />}
            {activeTab === "review" && <ReviewTab date={date} refreshKey={refreshKey} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
